import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import React, { FC, useCallback } from 'react';

interface ReportActionButtonProps {
	disableInvoiceReportPdf?: boolean;
	disableDownloadButtons?: boolean;
	showPdfButton?: boolean;
	downloadReport: (format: string) => Promise<void>;
	isDownloadingPdf?: boolean;
	isDownloadingCsv: boolean;
	isTransactionReport?: boolean;
	onReset: () => void;
}

export const ReportActionButton: FC<ReportActionButtonProps> = ({
	disableInvoiceReportPdf,
	disableDownloadButtons,
	showPdfButton = true,
	downloadReport,
	isDownloadingPdf,
	isDownloadingCsv,
	isTransactionReport,
	onReset,
}) => {
	const handleDownload = useCallback(
		(format: string) => {
			downloadReport(format);
		},
		[downloadReport],
	);

	const disablePdf = disableInvoiceReportPdf;
	const disabledTooltipText =
		'You must designate a facility using the search box above before downloading this report';

	return (
		<Grid container spacing={2} sx={{ display: 'flex', justifyContent: 'center', alignSelf: 'center', mt: 3 }}>
			<Grid item>
				<Tooltip title={disableDownloadButtons && disabledTooltipText}>
					{/* see https://stackoverflow.com/a/66713470 for why we wrap with span */}
					<span>
						<Button
							variant='contained'
							size='small'
							onClick={() => handleDownload('csv')}
							disabled={disableDownloadButtons || isDownloadingCsv}
							data-cy='download-csv-button'
						>
							{isDownloadingCsv ? (
								<CircularProgress size={24} />
							) : isTransactionReport ? (
								'Download Transaction Report'
							) : (
								'Download CSV'
							)}
						</Button>
					</span>
				</Tooltip>
			</Grid>
			{showPdfButton && !isTransactionReport && (
				<Grid item>
					<Tooltip title={disableDownloadButtons && disabledTooltipText}>
						<span>
							<Button
								variant='contained'
								size='small'
								onClick={() => handleDownload('pdf')}
								disabled={disableDownloadButtons || disablePdf || isDownloadingPdf}
								data-cy='download-pdf-button'
							>
								{isDownloadingPdf ? <CircularProgress size={24} /> : 'Download PDF'}
							</Button>
						</span>
					</Tooltip>
				</Grid>
			)}
			<Grid item>
				<Button
					variant='contained'
					size='small'
					fullWidth
					color='secondary'
					onClick={onReset}
					data-cy='reset-button'
				>
					Reset
				</Button>
			</Grid>
		</Grid>
	);
};
